import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import * as systemSettingsActions from './system-settings.actions';
import { SystemSettingsState, initialState } from './system-settings.state';

const reducer: ActionReducer<SystemSettingsState, Action> = createReducer(
  initialState,

  on(
    systemSettingsActions.getSystemSettings,
    systemSettingsActions.changePIN,
    systemSettingsActions.changeMainNotificationsRecipient,
    systemSettingsActions.changeAdditionalRecipients,
    systemSettingsActions.getAdmin,
    systemSettingsActions.removeAdmin,
    systemSettingsActions.updateAdmin,
    systemSettingsActions.createAdmin,
    systemSettingsActions.resendAdminActivation,
    systemSettingsActions.changeSecurityRecipients,
    systemSettingsActions.recreateAvatarsForAllUsers,
    systemSettingsActions.setSelfRegistrationStatus,
    systemSettingsActions.getSelfRegistrationStatus,
    systemSettingsActions.getModulesConfiguration,
    systemSettingsActions.getProducts,
    systemSettingsActions.updateProduct,
    systemSettingsActions.createProduct,
    (state): SystemSettingsState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),

  on(
    systemSettingsActions.getSystemSettingsSuccess,
    (state, { systemSettings }): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      systemSettings: { ...systemSettings },
    })
  ),

  on(
    systemSettingsActions.getSelfRegistrationStatusSuccess,
    (state, { isActive }): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      selfRegisterActive: isActive,
    })
  ),

  on(
    systemSettingsActions.getModulesConfigurationSuccess,
    (state, { modulesConfiguration }): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      modulesConfiguration: { ...modulesConfiguration },
    })
  ),

  on(
    systemSettingsActions.changePINSuccess,
    systemSettingsActions.changeMainNotificationsRecipientSuccess,
    systemSettingsActions.changeAdditionalRecipientsSuccess,
    systemSettingsActions.changeSecurityRecipientsSuccess,
    systemSettingsActions.setSelfRegistrationStatusSuccess,
    (state, { updatedSystemSettings }): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      systemSettings: { ...updatedSystemSettings },
    })
  ),

  on(
    systemSettingsActions.getAdminSuccess,
    (state, { admin }): SystemSettingsState => ({
      ...state,
      loadedAdminData: admin,
      loading: false,
      errorMessage: null,
    })
  ),

  on(
    systemSettingsActions.updateAdminSuccess,
    (state, { updatedAdmin }): SystemSettingsState => ({
      ...state,
      loadedAdminData: updatedAdmin,
      loadedAdmins: state.loadedAdmins.map(admin => (admin.id === updatedAdmin.id ? updatedAdmin : admin)),
      loading: false,
      errorMessage: null,
    })
  ),

  on(
    systemSettingsActions.createAdminSuccess,
    (state, { createdAdmin }): SystemSettingsState => ({
      ...state,
      loadedAdminData: createdAdmin,
      loading: false,
      errorMessage: null,
    })
  ),

  on(
    systemSettingsActions.removeAdminSuccess,
    (state, { removedAdminId }): SystemSettingsState => ({
      ...state,
      loadedAdmins: state.loadedAdmins.filter(admin => admin.id !== removedAdminId),
      loading: false,
      errorMessage: null,
    })
  ),

  on(
    systemSettingsActions.resendAdminActivationSuccess,
    (state): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
    })
  ),

  on(
    systemSettingsActions.getSystemSettingsError,
    systemSettingsActions.changePINError,
    systemSettingsActions.changeMainNotificationsRecipientError,
    systemSettingsActions.changeAdditionalRecipientsError,
    systemSettingsActions.getAdminError,
    systemSettingsActions.removeAdminError,
    systemSettingsActions.updateAdminError,
    systemSettingsActions.createAdminError,
    systemSettingsActions.resendAdminActivationError,
    systemSettingsActions.changeSecurityRecipientsError,
    systemSettingsActions.setSelfRegistrationStatusError,
    systemSettingsActions.getSelfRegistrationStatusError,
    systemSettingsActions.getModulesConfigurationError,
    (state, { errorMessage }): SystemSettingsState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    systemSettingsActions.getOnlineUsers,
    (state): SystemSettingsState => ({
      ...state,
      loadingAdmins: true,
      errorMessage: null,
    })
  ),

  on(systemSettingsActions.getOnlineUsersSuccess, (state, { users }): SystemSettingsState => {
    return {
      ...state,
      loadedOnlineUsers: users.data,
      loadedOnlineUsersPagination: users.pagination,
      loadingOnlineUsers: false,
      errorMessage: null,
    };
  }),

  on(
    systemSettingsActions.getOnlineUsersError,
    (state, { errorMessage }): SystemSettingsState => ({
      ...state,
      errorMessage,
      loadingOnlineUsers: false,
    })
  ),

  on(
    systemSettingsActions.getAdmins,
    (state): SystemSettingsState => ({
      ...state,
      loadingAdmins: true,
      errorMessage: null,
    })
  ),

  on(
    systemSettingsActions.getAdminsSuccess,
    (state, { admins }): SystemSettingsState => ({
      ...state,
      loadedAdmins: admins.data,
      loadedAdminsPagination: admins.pagination,
      loadingAdmins: false,
      errorMessage: null,
    })
  ),

  on(
    systemSettingsActions.getAdminsError,
    (state, { errorMessage }): SystemSettingsState => ({
      ...state,
      errorMessage,
      loadingAdmins: false,
    })
  ),

  on(
    systemSettingsActions.recreateAvatarsForAllUsersSuccess,
    (state): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    systemSettingsActions.recreateAvatarsForAllUsersError,
    (state): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: 'UANBLE_TO_RECREATE',
    })
  ),

  on(
    systemSettingsActions.getProductsSuccess,
    (state, { products }): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      products: structuredClone(products),
    })
  ),
  on(
    systemSettingsActions.getProductsError,
    (state): SystemSettingsState => ({
      ...state,
      loading: false,
    })
  ),
  on(
    systemSettingsActions.createProductSuccess,
    (state, { product }): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      products: [structuredClone(product), ...(state.products || [])],
    })
  ),
  on(
    systemSettingsActions.updateProductError,
    systemSettingsActions.createProductError,
    (state): SystemSettingsState => ({
      ...state,
      loading: false,
    })
  ),
  on(
    systemSettingsActions.updateProductSuccess,
    (state, { updatedProduct }): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      products: state.products.map(productInstate =>
        productInstate.id === updatedProduct.id ? structuredClone(updatedProduct) : productInstate
      ),
    })
  ),
  on(
    systemSettingsActions.updateProductError,
    (state): SystemSettingsState => ({
      ...state,
      loading: false,
    })
  ),
  on(
    systemSettingsActions.getProductHistory,
    (state, { productId }): SystemSettingsState => ({
      ...state,
      loading: true,
      errorMessage: null,
      productHistory: [],
    })
  ),
  on(
    systemSettingsActions.getProductHistorySuccess,
    (state, { productHistory }): SystemSettingsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      productHistory: structuredClone(productHistory),
    })
  ),
  on(
    systemSettingsActions.getProductHistoryError,
    (state, { productId }): SystemSettingsState => ({
      ...state,
      loading: false,
      productHistory: [],
    })
  ),

  // ############
  // PROMOTIONAL BANNER
  // ############

  on(
    systemSettingsActions.setPromotionalBanner,
    (state): SystemSettingsState => ({
      ...state,
      loadingPromotionalBanner: true,
    })
  ),
  on(
    systemSettingsActions.setPromotionalBannerSuccess,
    (state, { result }): SystemSettingsState => ({
      ...state,
      promotionalBanner: result,
      loadingPromotionalBanner: false,
    })
  ),
  on(
    systemSettingsActions.setPromotionalBannerError,
    (state, { errorMessage }): SystemSettingsState => ({
      ...state,
      errorMessage,
      promotionalBanner: null,
      loadingPromotionalBanner: false,
    })
  ),

  on(
    systemSettingsActions.getPromotionalBanner,
    (state): SystemSettingsState => ({
      ...state,
      loadingPromotionalBanner: true,
    })
  ),
  on(
    systemSettingsActions.getPromotionalBannerSuccess,
    (state, { result }): SystemSettingsState => ({
      ...state,
      promotionalBanner: result,
      loadingPromotionalBanner: false,
    })
  ),
  on(
    systemSettingsActions.getPromotionalBannerError,
    (state, { errorMessage }): SystemSettingsState => ({
      ...state,
      errorMessage,
      promotionalBanner: null,
      loadingPromotionalBanner: false,
    })
  ),

  on(
    systemSettingsActions.addReceivedPromotionalBanner,
    (state, { result }): SystemSettingsState => ({
      ...state,
      promotionalBanner: { ...result },
      loadingPromotionalBanner: false,
    })
  )
);

export function systemSettingsReducer(state: SystemSettingsState | undefined, action: Action): SystemSettingsState {
  return reducer(state, action);
}
